import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  CircularProgress,
  useTheme
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { twoFactorAPI } from '../../services/api';
import { logger } from '../../utils/logger';
import { TwoFactorFinishSetup } from './TwoFactorFinishSetup';
import { TwoFactorSetupResponse } from '../../types/api';

interface TwoFactorSetupProps {
  open: boolean;
  onClose: () => void;
  onSetupComplete: () => void;
}

export const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({
  open,
  onClose,
  onSetupComplete
}) => {
  const theme = useTheme();
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'setup' | 'backup-codes' | 'verify'>('setup');
  const [finishSetupOpen, setFinishSetupOpen] = useState(false);
  const [qrCodeBlobUrl, setQrCodeBlobUrl] = useState<string | null>(null);

  const handleSetup = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await twoFactorAPI.setup({});

      if (!response.qr_code) {
        setError('QR kod nije generisan od strane servera.');
        return;
      }

      // Create blob URL as alternative to data URL
      try {
        const binaryString = atob(response.qr_code);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.codePointAt(i) || 0;
        }
        const blob = new Blob([bytes], { type: 'image/png' });
        const blobUrl = URL.createObjectURL(blob);
        setQrCodeBlobUrl(blobUrl);
        logger.debug('Blob URL created');

        // Create canvas element as CSP-safe alternative
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx?.drawImage(img, 0, 0);
          logger.debug('Canvas QR code created');
        };

        img.onerror = (e) => {
          logger.error('Canvas image load error:', e);
          // Fallback - use data URL for canvas
          const dataImg = new Image();
          dataImg.onload = () => {
            canvas.width = dataImg.width;
            canvas.height = dataImg.height;
            ctx?.drawImage(dataImg, 0, 0);
            logger.debug('Canvas QR code created (data URL fallback)');
          };
          dataImg.src = `data:image/png;base64,${response.qr_code}`;
        };

        img.src = blobUrl;
      } catch (err) {
        logger.error('❌ Greška pri kreiranju blob URL/canvas:', err);
        // Direct canvas fallback
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();

          img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            logger.debug('Direct canvas QR code created');
          };

          img.src = `data:image/png;base64,${response.qr_code}`;
        } catch (canvasErr) {
          logger.error('❌ Canvas fallback greška:', canvasErr);
        }
      }

      setSetupData(response);
    } catch (err) {
      setError('Neuspešno podešavanje 2FA. Molimo pokušajte ponovo.');
      logger.error('2FA setup error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setStep('backup-codes');
  };

  const handleShowVerification = () => {
    setFinishSetupOpen(true);
  };

  const handleFinishSuccess = () => {
    onSetupComplete();
    onClose();
    setStep('setup');
    setSetupData(null);
    setFinishSetupOpen(false);
  };

  const handleComplete = () => {
    handleShowVerification();
  };

  const downloadBackupCodes = () => {
    if (!setupData) return;

    const codesText = setupData.backup_codes.join('\n');
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'omnipass-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadQRCode = () => {
    if (!setupData) return;

    const link = document.createElement('a');
    link.href = `data:image/png;base64,${setupData.qr_code}`;
    link.download = 'omnipass-qr-code.png';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  useEffect(() => {
    if (open && !setupData) {
      handleSetup();
    }
  }, [open, setupData]);

  // Cleanup blob URL when component unmounts or data changes
  useEffect(() => {
    return () => {
      if (qrCodeBlobUrl) {
        URL.revokeObjectURL(qrCodeBlobUrl);
      }
    };
  }, [qrCodeBlobUrl]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SecurityIcon color="primary" />
          Podešavanje Dvostruke Autentifikacije
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {setupData && step === 'setup' && (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Skenirajte ovaj QR kod sa aplikacijom za autentifikaciju (Google Authenticator, Authy, itd.):
            </Typography>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                  {setupData.qr_code ? (
                    <Box textAlign="center">
                      {/* Simple QR code display - should work now with fixed CSP */}
                      <Box mb={2}>
                        <img
                          src={`data:image/png;base64,${setupData.qr_code}`}
                          alt="2FA QR Code"
                          style={{
                            width: '200px',
                            height: '200px',
                            border: `2px solid ${theme.palette.primary.main}`,
                            borderRadius: '8px',
                            display: 'block',
                            margin: '0 auto'
                          }}
                          onError={(e) => {
                            logger.error('❌ QR kod se i dalje ne učitava:', e);
                          }}
                          onLoad={() => {
                          logger.debug('QR code loaded successfully');
                          }}
                        />
                        <Typography variant="caption" color="success.main" mt={1} display="block">
                          Skenirajte ovaj QR kod sa aplikacijom za autentifikaciju
                        </Typography>
                      </Box>

                      {/* Backup options */}
                      <Box mb={2}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `data:image/png;base64,${setupData.qr_code}`;
                            link.download = 'omnipass-qr-code.png';
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                          }}
                          sx={{ mr: 1 }}
                        >
                          💾 Preuzmi QR
                        </Button>

                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            window.open(`data:image/png;base64,${setupData.qr_code}`, '_blank');
                          }}
                        >
                          🔗 Otvori
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 200,
                        height: 200,
                        border: '2px dashed #f44336',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#ffebee',
                        borderRadius: 1
                      }}
                    >
                      <Typography variant="body2" color="error">
                        QR kod nije dostupan
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box display="flex" gap={1} mb={2}>
                  <Button size="small" variant="outlined" onClick={downloadQRCode}>
                    Preuzmi QR Kod
                  </Button>
                </Box>


              </CardContent>
            </Card>

            <Alert severity="info">
              <Typography variant="body2">
                Nakon skeniranja QR koda, dobićete rezervne kodove koji mogu biti korišćeni za pristup nalogu ako izgubite uređaj za autentifikaciju.
              </Typography>
            </Alert>
          </Box>
        )}

        {setupData && step === 'backup-codes' && (
          <Box>
            <Typography variant="h6" gutterBottom color="error">
              Važno: Sačuvajte Vaše Rezervne Kodove
            </Typography>

            <Typography variant="body1" sx={{ mb: 3 }}>
              Ovi rezervni kodovi mogu biti korišćeni za pristup nalogu ako izgubite uređaj za autentifikaciju.
              Svaki kod može biti korišćen samo jednom.
            </Typography>

            <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
              <CardContent>
                <Grid container spacing={1}>
                  {setupData.backup_codes.map((code: string) => (
                    <Grid key={code} size={{ xs: 6 }}>
                      <Chip
                        label={code}
                        variant="outlined"
                        sx={{
                          fontFamily: 'monospace',
                          width: '100%'
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            <Box display="flex" gap={2} mb={2}>
              <Button
                variant="outlined"
                onClick={downloadBackupCodes}
                fullWidth
              >
                Preuzmi Kodove
              </Button>
            </Box>

            <Alert severity="warning">
              <Typography variant="body2">
                Sačuvajte ove kodove na sigurnom mestu. Preporučujemo da ih odštampate ili sačuvate u sigurnom menadžeru lozinki.
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {step === 'setup' && setupData && (
          <>
            <Button onClick={onClose}>Otkaži</Button>
            <Button variant="contained" onClick={handleNext}>
              Skenirao sam QR Kod
            </Button>
          </>
        )}

        {step === 'backup-codes' && (
          <>
            <Button onClick={onClose}>Otkaži</Button>
            <Button variant="contained" onClick={handleComplete} color="primary">
              Nastavi sa Verifikacijom
            </Button>
          </>
        )}
      </DialogActions>

      {/* Finish Setup Dialog */}
      {setupData && (
        <TwoFactorFinishSetup
          open={finishSetupOpen}
          onClose={() => setFinishSetupOpen(false)}
          onSuccess={handleFinishSuccess}
          setupData={setupData}
        />
      )}
    </Dialog>
  );
};
